import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWebPush } from '@/hooks/useWebPush';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { useNotificationPrompt } from '@/hooks/useNotificationPrompt';
import { NotificationExplainerModal } from './NotificationExplainerModal';

interface ContextualNotificationPromptProps {
  context: 'streak' | 'trends' | 'goals' | 'achievement' | 'meditation' | 'journal' | 'breathwork' | 'sleep' | 'gratitude' | 'therapy' | 'challenges' | 'check-in' | 'cycle-tracker' | 'manifestation' | 'food-scanner' | 'medication';
  contextValue?: number | string;
  className?: string;
  triggerOnMount?: boolean;
}

const DISMISSED_KEY = 'notification_prompt_dismissed';

export function ContextualNotificationPrompt({ 
  context, 
  contextValue, 
  className,
  triggerOnMount = false
}: ContextualNotificationPromptProps) {
  const { isSupported, isSubscribed, subscribe } = useWebPush();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  
  // Use the notification prompt hook for first-time modal
  const { 
    shouldShowPrompt, 
    triggerContext,
    triggerPrompt, 
    closePrompt, 
    dismissPrompt,
    hasBeenShown 
  } = useNotificationPrompt();

  // Check if user has dismissed this type of prompt recently
  useEffect(() => {
    const dismissed = localStorage.getItem(`${DISMISSED_KEY}_${context}`);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      // Re-show after 7 days
      if (now.getTime() - dismissedDate.getTime() < 7 * 24 * 60 * 60 * 1000) {
        setIsDismissed(true);
      }
    }
  }, [context]);

  // Trigger the explainer modal on mount if this is a first-time trigger
  useEffect(() => {
    if (triggerOnMount && isNative && !hasBeenShown()) {
      const triggerContextMap: Record<string, 'check-in' | 'habit' | 'general'> = {
        'streak': 'check-in',
        'check-in': 'check-in',
        'meditation': 'habit',
        'journal': 'habit',
        'breathwork': 'habit',
        'sleep': 'habit',
        'gratitude': 'habit',
        'therapy': 'general',
        'challenges': 'general',
        'trends': 'general',
        'goals': 'general',
        'achievement': 'general'
      };
      triggerPrompt(triggerContextMap[context] || 'general');
    }
  }, [triggerOnMount, isNative, hasBeenShown, context, triggerPrompt]);

  const handleDismiss = () => {
    localStorage.setItem(`${DISMISSED_KEY}_${context}`, new Date().toISOString());
    setIsDismissed(true);
  };

  const handleEnableNotifications = async () => {
    if (isNative) {
      navigate('/settings');
      handleDismiss();
      return;
    }

    setIsSubscribing(true);
    try {
      await subscribe();
      handleDismiss();
    } catch (error) {
      console.error('Failed to subscribe:', error);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleViewOptions = () => {
    closePrompt();
    navigate('/settings');
  };

  const handleModalClose = () => {
    dismissPrompt();
  };

  const getContextMessage = () => {
    switch (context) {
      case 'streak':
      case 'check-in':
        if (contextValue && Number(contextValue) > 0) {
          return {
            title: 'Protect Your Streak',
            message: `You're on a ${contextValue}-day streak! Get reminders so you never miss a day.`
          };
        }
        return {
          title: 'Start Your Streak',
          message: 'Get daily reminders to build a consistent wellness habit.'
        };
      case 'trends':
        return {
          title: 'Stay Informed',
          message: 'Get weekly insights about your wellness patterns and progress.'
        };
      case 'goals':
        return {
          title: 'Reach Your Goals',
          message: 'Get gentle nudges to help you stay on track with your wellness goals.'
        };
      case 'achievement':
        return {
          title: 'Celebrate Milestones',
          message: 'Get notified when you unlock achievements and hit new milestones.'
        };
      case 'meditation':
        return {
          title: 'Deepen Your Practice',
          message: 'Get reminders to maintain your meditation routine.'
        };
      case 'journal':
        return {
          title: 'Capture Your Thoughts',
          message: 'Get gentle prompts to reflect and journal regularly.'
        };
      case 'breathwork':
        return {
          title: 'Breathe Better',
          message: 'Get reminders for calming breathwork sessions.'
        };
      case 'sleep':
        return {
          title: 'Sleep Better',
          message: 'Get bedtime reminders for better rest.'
        };
      case 'gratitude':
        return {
          title: 'Cultivate Gratitude',
          message: 'Get prompts to practice daily gratitude.'
        };
      case 'therapy':
        return {
          title: 'Continue Your Growth',
          message: 'Get reminders for check-ins with AI Therappi.'
        };
      case 'challenges':
        return {
          title: 'Stay on Track',
          message: 'Get updates on your wellness challenges.'
        };
      case 'cycle-tracker':
        return {
          title: 'Track Your Cycle',
          message: 'Get reminders for cycle tracking and lunar phases.'
        };
      case 'manifestation':
        return {
          title: 'Manifest Your Dreams',
          message: 'Get prompts to visualise and work towards your goals.'
        };
      case 'food-scanner':
        return {
          title: 'Nourish Mindfully',
          message: 'Get reminders to log meals and track nutrition.'
        };
      case 'medication':
        return {
          title: 'Never Miss a Dose',
          message: 'Get timely medication reminders.'
        };
      default:
        return {
          title: 'Stay Connected',
          message: 'Enable notifications for reminders and updates.'
        };
    }
  };

  const { title, message } = getContextMessage();

  // Don't show inline prompt if already subscribed, not supported, or dismissed
  // But still render the modal if shouldShowPrompt is true
  const showInlinePrompt = !isSubscribed && (isSupported || isNative) && !isDismissed;

  return (
    <>
      {/* First-time explainer modal for native platforms */}
      <NotificationExplainerModal
        isOpen={shouldShowPrompt}
        onClose={handleModalClose}
        onViewOptions={handleViewOptions}
        triggerContext={triggerContext}
      />

      {/* Inline prompt for returning users */}
      {showInlinePrompt && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={className}
          >
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
              <div className="p-2 rounded-full bg-primary/20">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground">{title}</p>
                <p className="text-xs text-muted-foreground truncate">{message}</p>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  onClick={handleEnableNotifications}
                  disabled={isSubscribing}
                  className="h-7 text-xs px-3"
                >
                  {isSubscribing ? '...' : 'Enable'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-7 w-7 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}
